/**
 * Screenshot PowerPoint tool: capture a slide as a PNG for visual context.
 *
 * Handlers follow the PowerPoint.run/load/sync conventions documented in
 * pptx-tools.ts.
 */

import type { PptxTool } from "../tool-types";

export const screenshotTools: PptxTool[] = [
  // ─── Get Slide Screenshot ─────────────────────────────────────────────────
  {
    // `Slide.getImageAsBase64` is PowerPointApi 1.8.
    minRequirementSet: "1.8",
    schema: {
      name: 'get_slide_screenshot',
      description:
        'Capture a slide as a PNG image for visual context. Defaults to the currently ' +
        'selected slide; pass slideIndex to capture a specific slide (falls back to the ' +
        'first slide when nothing is selected). Call this when you need to understand ' +
        'layout, positioning, or visual arrangement that cannot be determined from shape ' +
        'data alone. After this tool runs, the screenshot will be queued as an attachment ' +
        'in the chat — ask the user to send it so you can see the slide visually.',
      parameters: {
        type: 'object',
        properties: {
          slideIndex: {
            type: 'number',
            description: '0-based index of the slide to capture. Omit to use the selected slide.',
          },
          width: {
            type: 'number',
            description: 'Desired image width in pixels (default 640, max 1280).',
          },
        },
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        // getImageAsBase64 is a Slide method — requiring a *selection* was a
        // self-inflicted restriction. Resolve: explicit index → selected → slide 0.
        let slide: PowerPoint.Slide;
        if (typeof args.slideIndex === 'number') {
          const slides = context.presentation.slides;
          slides.load('items');
          await context.sync();
          if (args.slideIndex < 0 || args.slideIndex >= slides.items.length) {
            throw new Error(
              `slideIndex ${args.slideIndex} is out of range (presentation has ${slides.items.length} slides).`
            );
          }
          slide = slides.items[args.slideIndex as number];
        } else {
          const selectedSlides = context.presentation.getSelectedSlides();
          selectedSlides.load('items');
          await context.sync();
          if (selectedSlides.items.length > 0) {
            slide = selectedSlides.items[0];
          } else {
            const slides = context.presentation.slides;
            slides.load('items');
            await context.sync();
            if (slides.items.length === 0) {
              throw new Error('The presentation has no slides to capture.');
            }
            slide = slides.items[0];
          }
        }

        const requestedWidth = typeof args.width === 'number' ? args.width : 640;
        const width = Math.max(160, Math.min(requestedWidth, 1280));
        const img = slide.getImageAsBase64({ width });
        await context.sync();

        // Office.js getImageAsBase64() returns base64 with embedded line breaks
        // which cause atob() to fail in the iframe. Strip all whitespace first.
        const cleanBase64 = img.value.replace(/\s+/g, '');

        return {
          success: true,
          base64: cleanBase64,
          mimeType: 'image/png' as const,
          message: 'Screenshot captured and queued as an attachment in the chat input. Ask the user to send it so you can see the slide visually.',
        };
      });
    },
  },
];
